import React, { useEffect, useState } from 'react';

// Minimum viewport width (in CSS pixels) at which the Arena cockpit is
// considered usable. iPad mini portrait is 768 px and renders well, so
// the threshold sits just below that — every phone in portrait or
// landscape falls under it, every tablet sits above.
const MIN_SUPPORTED_WIDTH_PX = 700;
const QUERY = `(max-width: ${MIN_SUPPORTED_WIDTH_PX - 1}px)`;

// localStorage key for the "Continue anyway" choice. Persists across
// reloads so the citizen isn't nagged on every page transition. Bumping
// the version here invalidates the prior dismissal — useful if the
// threshold ever changes.
const DISMISS_KEY = 'agoracle:small-screen-warning:dismissed:v1';

function readMatch(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(QUERY).matches;
}

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

export default function SmallScreenWarning(): React.JSX.Element | null {
  const [isSmall,    setIsSmall]    = useState<boolean>(readMatch);
  const [dismissed,  setDismissed]  = useState<boolean>(readDismissed);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(QUERY);
    const onChange = (e: MediaQueryListEvent): void => setIsSmall(e.matches);
    setIsSmall(mql.matches);
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  if (!isSmall || dismissed) return null;

  function handleContinue(): void {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* private mode */ }
    setDismissed(true);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="small-screen-warning-title"
      style={{
        position:        'fixed',
        inset:           0,
        zIndex:          2147483646,
        background:      'rgba(2,6,23,0.94)',
        backdropFilter:  'blur(4px)',
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'center',
        padding:         '20px 16px',
        overflowY:       'auto',
        fontFamily:      "'Inter', -apple-system, system-ui, sans-serif",
      }}
    >
      <div
        style={{
          maxWidth:      420,
          width:         '100%',
          background:    'linear-gradient(180deg, #0b1326 0%, #060b1a 100%)',
          border:        '1px solid #b45309',
          borderRadius:  10,
          padding:       '22px 22px 18px',
          color:         '#e0e7ff',
          boxShadow:     '0 0 40px rgba(180,83,9,0.35), 0 0 0 1px rgba(252,211,77,0.18) inset',
        }}
      >
        <div style={{
          display:        'flex',
          alignItems:     'center',
          gap:            10,
          marginBottom:   14,
        }}>
          <span aria-hidden="true" style={{
            display:        'inline-flex',
            alignItems:     'center',
            justifyContent: 'center',
            width:          32,
            height:         32,
            borderRadius:   '50%',
            background:     'rgba(180,83,9,0.25)',
            border:         '1px solid #f59e0b',
            color:          '#fde68a',
            fontSize:       18,
            lineHeight:     1,
          }}>
            !
          </span>
          <h2
            id="small-screen-warning-title"
            style={{
              fontFamily:    'monospace',
              fontSize:      12,
              fontWeight:    700,
              letterSpacing: '0.22em',
              color:         '#fde68a',
              textTransform: 'uppercase',
              margin:        0,
            }}
          >
            Display Too Small
          </h2>
        </div>

        <p style={{
          fontSize:    14,
          lineHeight:  1.55,
          margin:      '0 0 12px',
          color:       '#cbd5f5',
        }}>
          The Arena cockpit is designed for tablets and larger displays.
          On a handheld phone, panels stack awkwardly and some controls
          can be hard to reach.
        </p>

        <div style={{
          background:    'rgba(15,23,42,0.65)',
          border:        '1px solid #1e293b',
          borderRadius:  6,
          padding:       '10px 12px',
          margin:        '0 0 16px',
          fontFamily:    'monospace',
          fontSize:      11,
          letterSpacing: '0.06em',
          color:         '#94a3b8',
        }}>
          <div>Recommended minimum width:&nbsp;
            <span style={{ color: '#fde68a', fontWeight: 700 }}>
              {MIN_SUPPORTED_WIDTH_PX}px
            </span>
          </div>
          <div style={{ marginTop: 4 }}>
            Best experience:&nbsp;
            <span style={{ color: '#a5b4fc' }}>iPad / desktop browser</span>
          </div>
        </div>

        <p style={{
          fontSize:    13,
          lineHeight:  1.5,
          margin:      '0 0 18px',
          color:       '#94a3b8',
        }}>
          You can still proceed on this device — most flows will work,
          but layout fidelity isn't guaranteed.
        </p>

        <div style={{
          display:         'flex',
          flexDirection:   'column',
          gap:             8,
        }}>
          <button
            type="button"
            onClick={handleContinue}
            style={{
              width:           '100%',
              padding:         '11px 16px',
              background:      'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
              border:          '1px solid #a78bfa',
              borderRadius:    6,
              color:           '#faf5ff',
              fontFamily:      'monospace',
              fontSize:        12,
              fontWeight:      700,
              letterSpacing:   '0.18em',
              textTransform:   'uppercase',
              cursor:          'pointer',
              boxShadow:       '0 0 14px rgba(124,58,237,0.35)',
            }}
          >
            Continue Anyway
          </button>
          <p style={{
            fontFamily:    'monospace',
            fontSize:      10,
            letterSpacing: '0.12em',
            color:         '#475569',
            margin:        '4px 0 0',
            textAlign:     'center',
          }}>
            Choice remembered on this device.
          </p>
        </div>
      </div>
    </div>
  );
}
